import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TextField,
  Alert,
  Container,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  TablePagination
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  CheckCircle,
  Print,
  Close,
  Save,
  Cancel,
  Receipt,
  Inventory,
  ExpandMore,
  LocalShipping,
  LocationOn,
  Note,
  Person,
  Phone,
  Email,
  CalendarToday,
  Update,
  AssignmentTurnedIn,
  CorporateFare,
  EventNote
} from "@mui/icons-material";
import { purchaseAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';

// Professional Print Styles
const printStyles = `
  @media print {
    @page {
      margin: 15mm;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-family: 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
      font-size: 12px;
      line-height: 1.4;
      color: #333;
    }
    
    .no-print {
      display: none !important;
    }
    
    .print-only {
      display: block !important;
    }
    
    .print-break-before {
      page-break-before: always;
    }
    
    .print-break-after {
      page-break-after: always;
    }
    
    .print-break-inside-avoid {
      page-break-inside: avoid;
    }
    
    /* Purchase Order Print Specific */
    .po-document {
      max-width: 100%;
      position: relative;
    }
    
    .company-header {
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 3px double #1976d2;
    }
    
    .company-name {
      font-size: 24px;
      font-weight: bold;
      color: #1976d2;
      letter-spacing: 1px;
      margin-bottom: 5px;
      text-transform: uppercase;
    }
    
    .document-title {
      text-align: center;
      margin: 20px 0 15px 0;
    }
    
    .document-title h1 {
      font-size: 20px;
      color: #1976d2;
      font-weight: bold;
      margin: 0;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    
    .document-meta {
      display: flex;
      justify-content: space-between;
      margin-bottom: 20px;
      padding: 10px;
      background: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 4px;
    }
    
    .meta-item {
      flex: 1;
      padding: 0 10px;
    }
    
    .meta-label {
      font-size: 10px;
      color: #6c757d;
      font-weight: bold;
      text-transform: uppercase;
      margin-bottom: 3px;
    }
    
    .meta-value {
      font-size: 11px;
      color: #333;
      font-weight: 500;
    }
    
    .info-section {
      margin-bottom: 20px;
      page-break-inside: avoid;
    }
    
    .info-section-title {
      font-size: 12px;
      font-weight: bold;
      color: #1976d2;
      border-bottom: 2px solid #1976d2;
      padding-bottom: 5px;
      margin-bottom: 10px;
      text-transform: uppercase;
    }
    
    .two-columns {
      display: flex;
      gap: 20px;
      margin-bottom: 15px;
    }
    
    .column {
      flex: 1;
    }
    
    .info-box {
      border: 1px solid #dee2e6;
      border-radius: 4px;
      padding: 12px;
      background: #f8f9fa;
    }
    
    .info-box-title {
      font-size: 11px;
      font-weight: bold;
      color: #495057;
      margin-bottom: 5px;
      text-transform: uppercase;
    }
    
    .info-box-content {
      font-size: 11px;
      line-height: 1.4;
    }
    
    .professional-table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0;
      font-size: 10px;
    }
    
    .professional-table th {
      background-color: #1976d2 !important;
      color: white !important;
      border: 1px solid #1976d2;
      padding: 8px 10px;
      text-align: left;
      font-weight: bold;
      text-transform: uppercase;
      font-size: 10px;
    }
    
    .professional-table td {
      border: 1px solid #dee2e6;
      padding: 8px 10px;
      vertical-align: top;
    }
    
    .professional-table tr:nth-child(even) {
      background-color: #f8f9fa;
    }
    
    .amount-cell {
      text-align: right;
      font-family: 'Courier New', monospace;
      font-weight: 500;
    }
    
    .totals-section {
      margin-top: 20px;
      margin-left: auto;
      max-width: 300px;
    }
    
    .total-line {
      display: flex;
      justify-content: space-between;
      padding: 6px 0;
      border-bottom: 1px solid #dee2e6;
    }
    
    .total-line.grand-total {
      border-top: 2px solid #1976d2;
      border-bottom: none;
      font-size: 12px;
      font-weight: bold;
      color: #1976d2;
      padding-top: 10px;
      margin-top: 5px;
    }
    
    .signature-section {
      margin-top: 40px;
      page-break-inside: avoid;
    }
    
    .signature-grid {
      display: flex;
      justify-content: space-between;
      margin-top: 20px;
    }
    
    .signature-box {
      text-align: center;
      flex: 1;
      margin: 0 10px;
    }
    
    .signature-line {
      height: 1px;
      background: #333;
      margin: 30px 0 5px 0;
    }
    
    .signature-label {
      font-size: 10px;
      font-weight: bold;
      margin-top: 5px;
      text-transform: uppercase;
    }
    
    .footer-section {
      margin-top: 50px;
      padding-top: 10px;
      border-top: 1px solid #dee2e6;
      text-align: center;
      font-size: 9px;
      color: #666;
    }
    
    .security-features {
      font-size: 8px;
      color: #999;
      border-top: 1px solid #eee;
      padding-top: 5px;
      margin-top: 10px;
    }
    
    .watermark {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-45deg);
      font-size: 60px;
      color: rgba(25, 118, 210, 0.1);
      pointer-events: none;
      z-index: -1;
      font-weight: bold;
      letter-spacing: 5px;
    }
    
    /* Hide Material-UI elements */
    .MuiCard-root, .MuiPaper-root {
      box-shadow: none !important;
      border: none !important;
    }
    
    .MuiButton-root, .MuiIconButton-root {
      display: none !important;
    }
    
    .MuiStepper-root {
      padding: 0 !important;
    }
  }
  
  @media screen {
    .print-only {
      display: none !important;
    }
  }
`;

export const InvPurchaseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole, user } = useAuth();
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGRNs, setLoadingGRNs] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editableData, setEditableData] = useState({
    deliveryInfo: '',
    termsAndConditions: '',
    shippingMethod: '',
    deliveryAddress: '',
    notes: ''
  });

  const printContentRef = useRef(null);

  useEffect(() => {
    fetchPurchaseOrder();
    fetchPurchaseOrderGRNs();
  }, [id]);

  const fetchPurchaseOrder = async () => {
    try {
      const response = await purchaseAPI.getById(id);
      const order = response.data.data.purchaseOrder;
      setPurchaseOrder(order);
      setEditableData({
        deliveryInfo: order.deliveryInfo || '',
        termsAndConditions: order.termsAndConditions || generateDefaultTerms(),
        shippingMethod: order.shippingMethod || '',
        deliveryAddress: order.deliveryAddress || '',
        notes: order.notes || ''
      });
    } catch (error) {
      enqueueSnackbar('Error fetching purchase order details', { variant: 'error' });
      navigate('/hospital/hospital-inventory/purchases');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrderGRNs = async () => {
    try {
      setLoadingGRNs(true);
      const response = await purchaseAPI.getGRNs(id);
      setGrns(response.data.data.grns || []);
    } catch (error) {
      enqueueSnackbar('Error fetching GRN details', { variant: 'error' });
    } finally {
      setLoadingGRNs(false);
    }
  };

  const generateDefaultTerms = () => {
    return `1. Goods must be delivered in original manufacturer's packaging with proper sealing
2. All items must have proper certification and documentation
3. Delivery must be accompanied by original invoice with batch details
4. Damaged or improperly stored goods will be rejected
5. Supplier must provide valid licenses and certificates for regulated items
6. All discrepancies must be reported within 24 hours of receipt
7. Payment terms: Net 30 days from date of satisfactory receipt
8. Quality standards must meet specified requirements`;
  };

  const getStatusSteps = () => {
    return ['Draft', 'Sent', 'Confirmed', 'Partially Received', 'Completed'];
  };

  const getActiveStep = (status) => {
    const statusMap = {
      'draft': 0,
      'sent': 1,
      'confirmed': 2,
      'partially_received': 3,
      'completed': 4
    };
    return statusMap[status] || 0;
  };

  const handlePrint = () => {
    setPrintDialogOpen(true);
  };

  const handleDirectPrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Purchase Order - ${purchaseOrder.poNumber}</title>
          <style>${printStyles}</style>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              color: #000;
            }
            .print-container { 
              max-width: 100%; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => window.close(), 1000);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleGrnDirectPrint = (grn) => {
    const printWindow = window.open('', '_blank');
    const printContent = generateProfessionalGRNPrintContent(grn);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>GRN - ${grn.grnNumber}</title>
          <style>${printStyles}</style>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              color: #333;
              background: white;
            }
            .print-container { 
              max-width: 100%; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              setTimeout(() => {
                window.print();
                setTimeout(() => window.close(), 500);
              }, 100);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setEditableData({
        deliveryInfo: purchaseOrder.deliveryInfo || '',
        termsAndConditions: purchaseOrder.termsAndConditions || generateDefaultTerms(),
        shippingMethod: purchaseOrder.shippingMethod || '',
        deliveryAddress: purchaseOrder.deliveryAddress || '',
        notes: purchaseOrder.notes || ''
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!purchaseOrder) return;

    setSaving(true);
    try {
      const updateData = {
        deliveryInfo: editableData.deliveryInfo,
        termsAndConditions: editableData.termsAndConditions,
        shippingMethod: editableData.shippingMethod,
        deliveryAddress: editableData.deliveryAddress,
        notes: editableData.notes
      };

      await purchaseAPI.update(id, updateData);

      setPurchaseOrder(prev => ({
        ...prev,
        ...updateData
      }));

      enqueueSnackbar('Purchase order updated successfully', { variant: 'success' });
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error updating purchase order';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditableChange = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const generateProfessionalGRNPrintContent = (grn) => {
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const printFormatCurrency = (amount) => {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
      }).format(amount || 0);
    };

    return `
      <div class="grn-document">
        <!-- Watermark -->
        <div class="watermark">GRN RECEIVED</div>
        
        <!-- Company Header -->
        <div class="company-header">
          <div style="text-align: center; margin-bottom: 15px;">
            <div class="company-name">Hospital Inventory Management</div>
            <div style="font-size: 14px; color: #666; font-weight: 500;">
              Goods Received Note (GRN)
            </div>
          </div>
        </div>
        
        <!-- Document Title -->
        <div class="document-title">
          <h1>GOODS RECEIVED NOTE</h1>
          <div style="font-size: 12px; color: #666;">
            Inventory Department
          </div>
        </div>
        
        <!-- Document Metadata -->
        <div class="document-meta">
          <div class="meta-item">
            <div class="meta-label">GRN Number</div>
            <div class="meta-value">${grn.grnNumber}</div>
          </div>
          <div class="meta-item">
            <div class="meta-label">PO Number</div>
            <div class="meta-value">${purchaseOrder?.poNumber || 'N/A'}</div>
          </div>
          <div class="meta-item">
            <div class="meta-label">Receipt Date</div>
            <div class="meta-value">${formatDate(grn.receiptDate)}</div>
          </div>
          <div class="meta-item">
            <div class="meta-label">Receiving Type</div>
            <div class="meta-value">${grn.receivingType === 'partial' ? 'PARTIAL RECEIVING' : 'COMPLETE RECEIVING'}</div>
          </div>
        </div>
        
        <!-- Company and Supplier Information -->
        <div class="two-columns info-section">
          <div class="column">
            <div class="info-section-title">RECEIVED BY (Consignee)</div>
            <div class="info-box">
              <div class="info-box-title">Hospital Inventory Management</div>
              <div class="info-box-content">
                123 Hospital Complex<br/>
                Medical City, Mumbai - 400001<br/>
                Phone: +91-22-1234-5678<br/>
                Email: inventory@hospital.com<br/>
                GSTIN: 27AAACM1234M1Z5
              </div>
            </div>
          </div>
          
          <div class="column">
            <div class="info-section-title">SUPPLIER (Consignor)</div>
            <div class="info-box">
              <div class="info-box-title">${purchaseOrder?.supplier?.name || 'N/A'}</div>
              <div class="info-box-content">
                ${purchaseOrder?.supplier?.address || 'Address not specified'}<br/>
                Phone: ${purchaseOrder?.supplier?.phone || 'N/A'}<br/>
                Email: ${purchaseOrder?.supplier?.email || 'N/A'}<br/>
                Contact Person: ${purchaseOrder?.supplier?.contactPerson || 'N/A'}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Received Items Table -->
        <div class="info-section">
          <div class="info-section-title">RECEIVED ITEMS DETAILS</div>
          <table class="professional-table">
            <thead>
              <tr>
                <th>Sr.</th>
                <th>Item Description</th>
                <th>Batch No.</th>
                <th>Expiry Date</th>
                <th>Ordered Qty</th>
                <th>Received Qty</th>
                <th>Unit Price</th>
                <th>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              ${grn.items?.map((item, index) => `
                <tr>
                  <td style="text-align: center;">${index + 1}</td>
                  <td>
                    <strong>${item.item?.name || 'N/A'}</strong><br/>
                    <small>${item.item?.brand || ''} • ${item.item?.itemType || ''}</small><br/>
                    <small>Unit: ${item.item?.unit || ''}</small>
                  </td>
                  <td style="text-align: center;">
                    <strong>${item.batchNumber || 'N/A'}</strong>
                  </td>
                  <td style="text-align: center;">
                    ${item.expiryDate ? new Date(item.expiryDate).toLocaleDateString('en-IN') : 'N/A'}
                  </td>
                  <td style="text-align: center;">${item.orderedQuantity || 0}</td>
                  <td style="text-align: center;">
                    <strong>${item.receivedQuantity || 0}</strong>
                  </td>
                  <td class="amount-cell">${printFormatCurrency(item.unitCost)}</td>
                  <td class="amount-cell">
                    <strong>${printFormatCurrency((item.receivedQuantity || 0) * (item.unitCost || 0))}</strong>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        <!-- Totals Section -->
        <div class="totals-section">
          <div class="total-line grand-total">
            <span>TOTAL RECEIVED VALUE:</span>
            <span class="amount-cell">${printFormatCurrency(grn.totalAmount || 0)}</span>
          </div>
        </div>
        
        <!-- Signatures Section -->
        <div class="signature-section">
          <div class="info-section-title">AUTHORIZED SIGNATURES</div>
          <div class="signature-grid">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">Received & Verified By</div>
              <div class="signature-name">${grn.receivedBy?.name || '___________________'}</div>
              <div class="signature-label">Date: ${new Date(grn.receiptDate).toLocaleDateString('en-IN')}</div>
            </div>
            
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">Supplier Representative</div>
              <div class="signature-name">___________________</div>
              <div class="signature-label">Date: ___________________</div>
            </div>
          </div>
          
          <!-- Company Stamp -->
          <div style="text-align: right; margin-top: 40px;">
            <div class="stamp-area">
              <div style="font-size: 14px; font-weight: bold; color: #2e7d32;">FOR HOSPITAL INVENTORY</div>
              <div style="font-size: 10px; margin-top: 5px;">Authorized Signatory</div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="footer-section">
          <div class="security-features">
            This is a computer generated document. No physical signature required.
          </div>
          <div>
            GRN: ${grn.grnNumber} | PO: ${purchaseOrder?.poNumber || 'N/A'} | 
            Generated on: ${new Date().toLocaleDateString('en-IN')}
          </div>
          <div style="margin-top: 10px; font-size: 8px; color: #999;">
            This document contains confidential information. Unauthorized distribution is prohibited.
          </div>
        </div>
      </div>
    `;
  };

  const calculateReceivedPercentage = () => {
    if (!purchaseOrder?.items) return 0;
    const totalOrdered = purchaseOrder.items.reduce((sum, item) => sum + item.quantity, 0);
    const totalReceived = purchaseOrder.items.reduce((sum, item) => sum + (item.receivedQuantity || 0), 0);
    return totalOrdered > 0 ? (totalReceived / totalOrdered) * 100 : 0;
  };

  const generatePrintContent = () => {
    if (!purchaseOrder) return '';

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const printFormatCurrency = (amount) => {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
      }).format(amount || 0);
    };

    const currentData = isEditing ? editableData : {
      deliveryInfo: purchaseOrder.deliveryInfo || '',
      termsAndConditions: purchaseOrder.termsAndConditions || generateDefaultTerms(),
      shippingMethod: purchaseOrder.shippingMethod || '',
      deliveryAddress: purchaseOrder.deliveryAddress || '',
      notes: purchaseOrder.notes || ''
    };

    return `
      <div class="po-document">
        <!-- Watermark -->
        <div class="watermark">PURCHASE ORDER</div>
        
        <!-- Header -->
        <div class="company-header">
          <div style="text-align: center; margin-bottom: 15px;">
            <div class="company-name">Hospital Inventory Management</div>
            <div style="font-size: 14px; color: #666; font-weight: 500;">
              Purchase Order
            </div>
          </div>
        </div>
        
        <!-- Document Title -->
        <div class="document-title">
          <h1>PURCHASE ORDER</h1>
          <div style="font-size: 12px; color: #666;">
            Inventory Department
          </div>
        </div>
        
        <!-- Document Metadata -->
        <div class="document-meta">
          <div class="meta-item">
            <div class="meta-label">PO Number</div>
            <div class="meta-value">${purchaseOrder.poNumber}</div>
          </div>
          <div class="meta-item">
            <div class="meta-label">Order Date</div>
            <div class="meta-value">${formatDate(purchaseOrder.orderDate)}</div>
          </div>
          ${purchaseOrder.expectedDeliveryDate ? `
            <div class="meta-item">
              <div class="meta-label">Expected Delivery</div>
              <div class="meta-value">${formatDate(purchaseOrder.expectedDeliveryDate)}</div>
            </div>
          ` : ''}
          <div class="meta-item">
            <div class="meta-label">Status</div>
            <div class="meta-value">${purchaseOrder.status.replace('_', ' ').toUpperCase()}</div>
          </div>
        </div>
        
        <!-- Company and Supplier Info -->
        <div class="two-columns info-section">
          <div class="column">
            <div class="info-section-title">FROM</div>
            <div class="info-box">
              <div class="info-box-title">Hospital Inventory Management</div>
              <div class="info-box-content">
                123 Hospital Complex<br/>
                Medical City, Mumbai - 400001<br/>
                Phone: +91-22-1234-5678<br/>
                Email: inventory@hospital.com<br/>
                GSTIN: 27AAACM1234M1Z5
              </div>
            </div>
          </div>
          
          <div class="column">
            <div class="info-section-title">TO</div>
            <div class="info-box">
              <div class="info-box-title">${purchaseOrder.supplier.name}</div>
              <div class="info-box-content">
                ${purchaseOrder.supplier.address || 'Address not specified'}<br/>
                Phone: ${purchaseOrder.supplier.phone || 'N/A'}<br/>
                Email: ${purchaseOrder.supplier.email || 'N/A'}<br/>
                Contact Person: ${purchaseOrder.supplier.contactPerson || 'N/A'}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Order Items -->
        <div class="info-section">
          <div class="info-section-title">ORDER ITEMS</div>
          <table class="professional-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item Description</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Tax Rate</th>
                <th>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              ${purchaseOrder.items.map((item, index) => `
                <tr>
                  <td style="text-align: center;">${index + 1}</td>
                  <td>
                    <strong>${item.item?.name || 'N/A'}</strong><br/>
                    <small>${item.item?.brand || ''} • ${item.item?.itemType || ''}</small><br/>
                    <small>Unit: ${item.item?.unit || ''}</small>
                  </td>
                  <td style="text-align: center;">${item.quantity}</td>
                  <td class="amount-cell">${printFormatCurrency(item.costPrice)}</td>
                  <td style="text-align: center;">${item.taxRate || 0}%</td>
                  <td class="amount-cell">${printFormatCurrency(item.quantity * item.costPrice)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        <!-- Totals -->
        <div class="totals-section">
          <div class="total-line">
            <span>Subtotal:</span>
            <span class="amount-cell">${printFormatCurrency(purchaseOrder.totalAmount)}</span>
          </div>
          ${purchaseOrder.taxAmount > 0 ? `
            <div class="total-line">
              <span>Tax Amount:</span>
              <span class="amount-cell">${printFormatCurrency(purchaseOrder.taxAmount)}</span>
            </div>
          ` : ''}
          <div class="total-line grand-total">
            <span>GRAND TOTAL:</span>
            <span class="amount-cell">${printFormatCurrency(purchaseOrder.grandTotal)}</span>
          </div>
        </div>
        
        <!-- Additional Information -->
        <div class="two-columns info-section" style="page-break-before: always;">
          <div class="column">
            <div class="info-section-title">DELIVERY INFORMATION</div>
            <div class="info-box" style="white-space: pre-line;">
              ${currentData.deliveryInfo || 
                `Expected Delivery: ${purchaseOrder.expectedDeliveryDate ? formatDate(purchaseOrder.expectedDeliveryDate) : 'Not specified'}
                
Delivery Address:
Hospital Inventory Management
123 Hospital Complex
Medical City, Mumbai - 400001`
              }
            </div>
          </div>
          
          <div class="column">
            <div class="info-section-title">TERMS & CONDITIONS</div>
            <div class="info-box" style="white-space: pre-line;">
              ${currentData.termsAndConditions}
              ${currentData.notes ? `\n\nSpecial Notes:\n${currentData.notes}` : ''}
            </div>
          </div>
        </div>
        
        <!-- Signatures -->
        <div class="signature-section">
          <div class="info-section-title">AUTHORIZED SIGNATURES</div>
          <div class="signature-grid">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">Authorized Signatory</div>
              <div class="signature-label">(Supplier)</div>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">Authorized Signatory</div>
              <div class="signature-label">(Hospital Inventory)</div>
            </div>
          </div>
          
          <!-- Company Stamp -->
          <div style="text-align: right; margin-top: 40px;">
            <div style="border: 2px solid #1976d2; padding: 15px; display: inline-block; text-align: center;">
              <div style="font-size: 14px; font-weight: bold; color: #1976d2;">FOR HOSPITAL INVENTORY</div>
              <div style="font-size: 10px; margin-top: 5px;">Authorized Signatory</div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="footer-section">
          <div class="security-features">
            This is a computer generated document. No physical signature required.
          </div>
          <div>
            PO: ${purchaseOrder.poNumber} | Generated on: ${new Date().toLocaleDateString('en-IN')}
          </div>
          <div style="margin-top: 10px; font-size: 8px; color: #999;">
            This document contains confidential information. Unauthorized distribution is prohibited.
          </div>
        </div>
      </div>
    `;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!purchaseOrder) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h4" color="error" sx={{ fontSize: '1.5rem' }}>
          Purchase order not found
        </Typography>
        <Button
          onClick={() => navigate('/hospital/hospital-inventory/purchases')}
          sx={{ mt: 2, fontSize: '1.1rem' }}
        >
          Back to Purchase Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <style>{printStyles}</style>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }} className="no-print">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBack sx={{ fontSize: 28 }} />}
              onClick={() => navigate('/hospital/hospital-inventory/purchases')}
              sx={{ fontSize: '1.1rem' }}
            >
              Back
            </Button>
            <Box>
              <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
                Purchase Order: {purchaseOrder.poNumber}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                <Chip
                  label={purchaseOrder.status.replace('_', ' ')}
                  color={
                    purchaseOrder.status === 'completed' ? 'success' :
                      purchaseOrder.status === 'cancelled' ? 'error' :
                        purchaseOrder.status === 'partially_received' ? 'warning' : 'primary'
                  }
                  sx={{ fontSize: '1.1rem', height: '36px' }}
                />
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  {purchaseOrder.supplier?.name}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Print Purchase Order">
              <Button
                startIcon={<Print sx={{ fontSize: 24 }} />}
                onClick={handlePrint}
                variant="outlined"
                sx={{ fontSize: '1.1rem', py: 1.5 }}
              >
                Print PO
              </Button>
            </Tooltip>

            {hasAnyRole(['admin', 'inventory_manager']) && (
              <>
                {isEditing ? (
                  <>
                    <Button
                      startIcon={<Cancel sx={{ fontSize: 24 }} />}
                      onClick={handleEditToggle}
                      variant="outlined"
                      color="secondary"
                      sx={{ fontSize: '1.1rem', py: 1.5 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      startIcon={<Save sx={{ fontSize: 24 }} />}
                      onClick={handleSave}
                      variant="contained"
                      disabled={saving}
                      sx={{ fontSize: '1.1rem', py: 1.5 }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <Button
                    startIcon={<Edit sx={{ fontSize: 24 }} />}
                    onClick={handleEditToggle}
                    variant="contained"
                    sx={{ fontSize: '1.1rem', py: 1.5 }}
                  >
                    Edit Details
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>

        {isEditing && (
          <Alert severity="info" sx={{
            mb: 2,
            fontSize: '1.1rem',
            '& .MuiAlert-message': { fontSize: '1.1rem' }
          }} className="no-print">
            You are editing delivery information and terms & conditions. These changes will be reflected in printed documents.
          </Alert>
        )}

        {/* Receiving Progress & GRN Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
              <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
              Receiving History & GRNs
            </Typography>

            {/* Progress Bar */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                  Receiving Progress
                </Typography>
                <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem' }}>
                  ${calculateReceivedPercentage().toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={calculateReceivedPercentage()}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  mb: 2,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: calculateReceivedPercentage() === 100 ? '#2e7d32' :
                      calculateReceivedPercentage() > 0 ? '#ed6c02' : '#1976d2'
                  }
                }}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Ordered
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {purchaseOrder.items.reduce((sum, item) => sum + item.quantity, 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="body2" color="success.main">
                      Received
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {purchaseOrder.items.reduce((sum, item) => sum + (item.receivedQuantity || 0), 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="body2" color={calculateReceivedPercentage() < 100 ? "warning.main" : "success.main"}>
                      Pending
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color={calculateReceivedPercentage() < 100 ? "warning.main" : "success.main"}>
                      {purchaseOrder.items.reduce((sum, item) => sum + (item.quantity - (item.receivedQuantity || 0)), 0)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* GRN List */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontSize: '1.3rem' }}>
                  <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Goods Received Notes (GRNs)
                </Typography>
                <Chip
                  label={`Total: ${grns.length} GRN${grns.length !== 1 ? 's' : ''}`}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {loadingGRNs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : grns.length === 0 ? (
                <Alert severity="info" sx={{ fontSize: '1.1rem' }}>
                  No GRNs generated yet. GRNs will be created when items are received.
                </Alert>
              ) : (
                <>
                  <Grid container spacing={2}>
                    {grns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((grn) => (
                      <Grid item xs={12} key={grn._id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderLeft: `4px solid ${grn.receivingType === 'partial' ? '#ff9800' : '#2e7d32'}`,
                            '&:hover': {
                              boxShadow: 2,
                              borderColor: 'primary.main'
                            }
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                                    {grn.grnNumber}
                                  </Typography>
                                  <Chip
                                    label={grn.receivingType === 'partial' ? 'Partial' : 'Complete'}
                                    color={grn.receivingType === 'partial' ? 'warning' : 'success'}
                                    size="small"
                                  />
                                  {grn.receivingType === 'partial' && grn.partialReceivingNumber && (
                                    <Chip
                                      label={`Batch #${grn.partialReceivingNumber}`}
                                      variant="outlined"
                                      size="small"
                                    />
                                  )}
                                </Box>

                                <Grid container spacing={1} sx={{ mb: 1 }}>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CalendarToday fontSize="small" color="action" />
                                      <Typography variant="body2" color="text.secondary">
                                        Date: {new Date(grn.receiptDate).toLocaleDateString()}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <EventNote fontSize="small" color="action" />
                                      <Typography variant="body2" color="text.secondary">
                                        Items: {grn.items?.length || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Receipt fontSize="small" color="action" />
                                      <Typography variant="body2" color="text.secondary">
                                        Value: ₹{grn.totalAmount?.toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Person fontSize="small" color="action" />
                                      <Typography variant="body2" color="text.secondary">
                                        By: {grn.receivedBy?.name || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title="Print GRN">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleGrnDirectPrint(grn)}
                                    sx={{
                                      border: '1px solid',
                                      borderColor: 'primary.main',
                                      '&:hover': { bgcolor: 'primary.main', color: 'white' }
                                    }}
                                  >
                                    <Print fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>

                            {/* GRN Items Accordion */}
                            <Accordion sx={{ mt: 2, boxShadow: 'none', bgcolor: 'transparent' }}>
                              <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="body2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Inventory fontSize="small" />
                                  View Received Items ({grn.items?.length || 0})
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ pt: 0 }}>
                                <TableContainer component={Paper} variant="outlined">
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell><strong>Item</strong></TableCell>
                                        <TableCell align="center"><strong>Batch No.</strong></TableCell>
                                        <TableCell align="center"><strong>Expiry</strong></TableCell>
                                        <TableCell align="center"><strong>Ordered</strong></TableCell>
                                        <TableCell align="center"><strong>Received</strong></TableCell>
                                        <TableCell align="right"><strong>Unit Cost</strong></TableCell>
                                        <TableCell align="right"><strong>Total</strong></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {grn.items?.map((item, idx) => (
                                        <TableRow key={idx} hover>
                                          <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                              {item.item?.name || 'N/A'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {item.item?.brand || ''} • {item.item?.itemType || ''}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Chip
                                              label={item.batchNumber || 'N/A'}
                                              size="small"
                                              variant="outlined"
                                            />
                                          </TableCell>
                                          <TableCell align="center">
                                            {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : 'N/A'}
                                          </TableCell>
                                          <TableCell align="center">
                                            {item.orderedQuantity || 0}
                                          </TableCell>
                                          <TableCell align="center">
                                            <Chip
                                              label={item.receivedQuantity || 0}
                                              size="small"
                                              color="success"
                                              variant="filled"
                                            />
                                          </TableCell>
                                          <TableCell align="right">
                                            ₹{item.unitCost || 0}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                              ₹{((item.receivedQuantity || 0) * (item.unitCost || 0)).toFixed(2)}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell colSpan={6} align="right">
                                          <Typography variant="body1" fontWeight="bold">
                                            Total:
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body1" fontWeight="bold" color="primary">
                                            ₹{grn.totalAmount?.toFixed(2)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </AccordionDetails>
                            </Accordion>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  <TablePagination
                    component="div"
                    count={grns.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    sx={{ mt: 2 }}
                  />
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Print Preview Dialog */}
        <Dialog
          open={printDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
              Print Preview - {purchaseOrder.poNumber}
            </Typography>
            <IconButton onClick={() => setPrintDialogOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              ref={printContentRef}
              sx={{
                height: '100%',
                overflow: 'auto',
                '& .print-container': {
                  p: 2,
                  bgcolor: 'background.paper',
                },
              }}
              dangerouslySetInnerHTML={{ __html: generatePrintContent() }}
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
              This is a preview. Actual print may vary slightly.
            </Typography>
            <Box>
              <Button onClick={() => setPrintDialogOpen(false)} sx={{ mr: 1, fontSize: '1.1rem' }}>
                Cancel
              </Button>
              <Button
                onClick={handleDirectPrint}
                variant="contained"
                startIcon={<Print />}
                sx={{ fontSize: '1.1rem' }}
              >
                Print Now
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Main Content - Hidden during print */}
        <div className="no-print">
          <Grid container spacing={3}>
            {/* Main Details */}
            <Grid item xs={12} md={8}>
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    Order Items
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Item</TableCell>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Description</TableCell>
                          <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Quantity</TableCell>
                          <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Unit Price</TableCell>
                          <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Received</TableCell>
                          <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {purchaseOrder.items.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {item.item?.name || 'N/A'}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                SKU: {item.item?.sku || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                {item.item?.brand || ''}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                {item.item?.itemType} • {item.item?.unit}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                ₹{item.costPrice}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Chip
                                label={item.receivedQuantity || 0}
                                color={item.receivedQuantity >= item.quantity ? "success" : "warning"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                ₹{(item.quantity * item.costPrice).toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 3, textAlign: 'right' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1.2rem' }}>
                      Subtotal: ₹{purchaseOrder.totalAmount?.toFixed(2)}
                    </Typography>
                    {purchaseOrder.taxAmount > 0 && (
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '1.2rem' }}>
                        Tax: ₹{purchaseOrder.taxAmount?.toFixed(2)}
                      </Typography>
                    )}
                    <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.4rem', borderTop: '2px solid', borderColor: 'divider', pt: 1, mt: 1 }}>
                      Grand Total: ₹{purchaseOrder.grandTotal?.toFixed(2)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Shipping & Delivery Information */}
              <Card>
                <CardContent sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                      <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Delivery Information
                    </Typography>
                    {isEditing && (
                      <Chip label="Editing" color="primary" size="medium" sx={{ fontSize: '1.1rem' }} />
                    )}
                  </Box>

                  {isEditing ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Shipping Method"
                          value={editableData.shippingMethod}
                          onChange={(e) => handleEditableChange('shippingMethod', e.target.value)}
                          placeholder="e.g., Express, Standard, etc."
                          InputProps={{
                            startAdornment: <LocalShipping color="action" sx={{ mr: 1 }} />,
                            sx: { fontSize: '1.1rem' }
                          }}
                          InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Expected Delivery Date"
                          value={purchaseOrder.expectedDeliveryDate ? purchaseOrder.expectedDeliveryDate.split('T')[0] : ''}
                          InputLabelProps={{ shrink: true }}
                          disabled
                          helperText="Edit in main purchase order"
                          InputProps={{
                            startAdornment: <CalendarToday color="action" sx={{ mr: 1 }} />,
                            sx: { fontSize: '1.1rem' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Delivery Address"
                          value={editableData.deliveryAddress}
                          onChange={(e) => handleEditableChange('deliveryAddress', e.target.value)}
                          placeholder="Enter complete delivery address"
                          InputProps={{
                            startAdornment: <LocationOn color="action" sx={{ mr: 1, mt: 2 }} />,
                            sx: { fontSize: '1.1rem' }
                          }}
                          InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Delivery Instructions & Information"
                          value={editableData.deliveryInfo}
                          onChange={(e) => handleEditableChange('deliveryInfo', e.target.value)}
                          placeholder="Enter any special delivery instructions, contact information, or additional delivery details..."
                          helperText="This will appear in the printed purchase order"
                          InputProps={{
                            sx: { fontSize: '1.1rem' }
                          }}
                          InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LocalShipping color="action" />
                          <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            Shipping Method
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontSize: '1.2rem', pl: 4 }}>
                          {purchaseOrder.shippingMethod || 'Not specified'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CalendarToday color="action" />
                          <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            Expected Delivery Date
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontSize: '1.2rem', pl: 4 }}>
                          {purchaseOrder.expectedDeliveryDate ?
                            new Date(purchaseOrder.expectedDeliveryDate).toLocaleDateString() :
                            'Not specified'
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LocationOn color="action" />
                          <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            Delivery Address
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontSize: '1.2rem', pl: 4 }}>
                          {purchaseOrder.deliveryAddress || 'Not specified'}
                        </Typography>
                      </Grid>
                      {purchaseOrder.deliveryInfo && (
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Note color="action" />
                            <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                              Additional Delivery Information
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontSize: '1.2rem', pl: 4 }}>
                            {purchaseOrder.deliveryInfo}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              {/* Order Timeline */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    Order Status Timeline
                  </Typography>
                  <Stepper activeStep={getActiveStep(purchaseOrder.status)} orientation="vertical">
                    {getStatusSteps().map((step, index) => (
                      <Step key={step}>
                        <StepLabel
                          StepIconComponent={CheckCircle}
                          sx={{
                            '& .MuiStepLabel-label': {
                              fontSize: '1.1rem',
                              fontWeight: index <= getActiveStep(purchaseOrder.status) ? 'bold' : 'normal'
                            },
                            '& .MuiStepIcon-root': {
                              fontSize: '2rem',
                              color: index <= getActiveStep(purchaseOrder.status) ? 'primary.main' : 'grey.400'
                            }
                          }}
                        >
                          {step}
                        </StepLabel>
                        <StepContent>
                          {index === getActiveStep(purchaseOrder.status) && (
                            <Typography variant="body2" color="primary" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                              Current status
                            </Typography>
                          )}
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>
                </CardContent>
              </Card>

              {/* Terms & Conditions */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                      <AssignmentTurnedIn sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Terms & Conditions
                    </Typography>
                    {isEditing && (
                      <Chip label="Editing" color="primary" size="medium" sx={{ fontSize: '1.1rem' }} />
                    )}
                  </Box>

                  {isEditing ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Terms & Conditions"
                        value={editableData.termsAndConditions}
                        onChange={(e) => handleEditableChange('termsAndConditions', e.target.value)}
                        placeholder="Enter terms and conditions (one per line)"
                        helperText="Each line will be treated as a separate term"
                        InputProps={{
                          sx: { fontSize: '1.1rem' }
                        }}
                        InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Additional Notes"
                        value={editableData.notes}
                        onChange={(e) => handleEditableChange('notes', e.target.value)}
                        placeholder="Any additional notes or special instructions..."
                        sx={{ mt: 2 }}
                        InputProps={{
                          startAdornment: <Note color="action" sx={{ mr: 1, mt: 2 }} />,
                          sx: { fontSize: '1.1rem' }
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2, fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {purchaseOrder.termsAndConditions || generateDefaultTerms()}
                      </Typography>
                      {purchaseOrder.notes && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Note color="action" />
                            <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                              Additional Notes:
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontSize: '1.1rem', pl: 4 }}>
                            {purchaseOrder.notes}
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Supplier Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    <CorporateFare sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Supplier Information
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Person color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Supplier Name
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', pl: 4 }}>
                        {purchaseOrder.supplier.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Person color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Contact Person
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', pl: 4 }}>
                        {purchaseOrder.supplier.contactPerson}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Email color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', pl: 4 }}>
                        {purchaseOrder.supplier.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Phone color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Phone
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', pl: 4 }}>
                        {purchaseOrder.supplier.phone}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Order Information */}
              <Card>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    <EventNote sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Order Information
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarToday color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Order Date
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', pl: 4 }}>
                        {new Date(purchaseOrder.orderDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Person color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Created By
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', pl: 4 }}>
                        {purchaseOrder.createdBy?.name || 'System'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Update color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Last Updated
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', pl: 4 }}>
                        {new Date(purchaseOrder.updatedAt).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </div>
      </Container>
    </Box>
  );
};